/**
 * TOSS Web Application - Internal-Only Route Guard
 *
 * Refuses the external client audience on internal pages that no permission key
 * happens to gate. Most internal routes are already safe once permissionStore
 * correctly resolves a client session to an empty key set (PermissionRoute then
 * denies by construction) — but a handful of pages are reachable by *any*
 * authenticated internal role with no key requirement at all, because every staff
 * role is allowed its own profile/notifications: `/profile`, `/notifications`,
 * `/share`. Those are the routes this guard exists for
 * (phase0-part2-client-role-and-schema.md §2.5).
 *
 * The inverse of ClientRoute (which requires role === Client, this requires
 * role !== Client) — kept as a separate component rather than a shared "role
 * must/must-not equal X" parameter, since the two are used in unrelated places
 * and a shared abstraction would buy little.
 *
 * @example
 * ```tsx
 * <InternalOnlyRoute>
 *   <ProfilePage />
 * </InternalOnlyRoute>
 * ```
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  useIsAuthenticated,
  useAuthStore,
  selectIsInitializing,
  selectIsAuthPermissionsLoaded,
} from '@/store';
import { UserRole } from '@/types';
import { ROUTES } from '@/constants';
import { PageLoading } from '@/components/ui';
import { ProtectedRoute } from './ProtectedRoute';

// ============================================================================
// Types
// ============================================================================

export interface InternalOnlyRouteProps {
  children: ReactNode;
  /** Where to redirect a client session. @default ROUTES.errors.forbidden */
  fallbackTo?: string;
}

// ============================================================================
// Component
// ============================================================================

export function InternalOnlyRoute({
  children,
  fallbackTo = ROUTES.errors.forbidden,
}: InternalOnlyRouteProps): JSX.Element {
  const isAuthenticated = useIsAuthenticated();
  const isInitializing = useAuthStore(selectIsInitializing);
  const isPermissionsLoaded = useAuthStore(selectIsAuthPermissionsLoaded);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  if (isInitializing) {
    return <PageLoading />;
  }

  if (!isAuthenticated) {
    return <ProtectedRoute>{children}</ProtectedRoute>;
  }

  if (!isPermissionsLoaded) {
    return <PageLoading />;
  }

  if (user?.role === UserRole.Client) {
    return (
      <Navigate to={fallbackTo} state={{ from: location, accessDenied: true }} replace />
    );
  }

  return <>{children}</>;
}

export default InternalOnlyRoute;
